<template>
    <div class="form-input" :class="[
                sizeableString ? 'form-input-' + sizeableString : '', {
                'is-active': isActive,
                'is-required': isRequired,
                'is-empty': isEmpty,
                'has-error': hasError,
                'has-warning': !hasError && props.warning,
                'is-inverted': props.inverted,
                'is-plain': props.plain,
                'is-disabled': props.disabled,
                'has-suffix': hasSuffix,
                'has-prefix': hasPrefix,
            }]">

        <div v-if="$slots.default" @click="showPickerModal = true">
            <slot/>
        </div>
        <template v-else>
            <div class="form-input__content">
                <div v-if="hasPrefix" class="form-input__prefix">
                    <Icon v-if="props.icon" :icon="props.icon"/>
                </div>
                <label class="form-input__field-wrapper">
                    <span v-if="props.label" class="form-input__label">{{props.label}}</span>
                    <input class="form-input__field" :class="inputClass" ref="input" type="text" :autofocus="props.autofocus" :name="props.name" v-model="inputValue"
                           readonly
                           :placeholder="formatPlaceholder && isActive ? formatPlaceholder : props.placeholder" :disabled="props.disabled" @keydown="handleKeydown" @blur="handleBlur" @focus="handleFocus"/>
                </label>
                <div v-if="hasSuffix" class="form-input__suffix">
                    <Icon v-if="!props.disabled && props.clearable && submitValue" :icon="icons.reset" class="cursor-pointer" :class="{'mr-4': !!props.help}" @click="clearValue"/>
                    <tooltip v-if="props.help" :text="props.help" left with-touch>
                        <Icon :icon="icons.help"/>
                    </tooltip>
                </div>
            </div>
            <div v-if="!hideDetails" class="form-input__details">
                <span v-if="hasError && errorMessage" class="form-input__message" tabindex="-1">{{ errorMessage }}</span>
                <span v-else-if="props.warning" class="form-input__message" tabindex="-1">{{ props.warning }}</span>
                <span v-else-if="props.info" class="form-input__message" tabindex="-1">{{ props.info }}</span>
            </div>
        </template>

        <popup v-model="showPickerModal" highest-order>
            <card width="350">
                <card-text fill class="border-box overflow-y-auto form-date-input-picker flex flex-column h-full p-0">
                    <div v-if="props.label" class="p-16 flex flex-column" :class="{'bg-primary': !props.color}" :style="{backgroundColor: props.color}">
                        <span class="text-sm text-white">{{ props.label }}</span>
                        <div class="flex justify-between mt-8">
                            <span class="text-lg text-white cursor-pointer" @click="showTimePicker = false">{{ submitValue && date ? date.format('dd., D. MMM YYYY') : (placeholder ? placeholder : '-') }}</span>
                            <span v-if="props.time && pickerDate.hour && submitValue && date" class="text-lg text-white cursor-pointer" @click="showTimePicker = true">{{ date.format(props.seconds ? 'HH:mm:ss' : 'HH:mm') }}</span>
                        </div>
                    </div>

                    <div class="bg-white flex flex-column" :class="{'items-center justify-center': showTimePicker}">
                        <div class="w-full">
                            <lazy-time-picker v-if="showTimePicker" v-model="pickerDate" :color="props.color" :even-minutes="props.evenMinutes" @selected-hour="handleSelectedPickerHour" @selected-minute="handleSelectedPickerMinute" class="py-10 px-16"/>
                            <lazy-date-picker v-else v-model="pickerDate" :color="props.color" :max="props.rules ? props.rules.max : null" :min="props.rules ? props.rules.min : null" v-model:mode="pickerDateMode" @selected="handleSelectedPickerDate"/>
                        </div>

                        <div v-if="$device.isDesktop" class="px-16 w-full">
                            <lazy-form-input v-model="inputValue" autofocus hide-details inverted :placeholder="formatPlaceholder" type="text"
                                        class="mt-8"
                                        :mask="maskFormat"
                                        :clearable="props.clearable"
                                        @reset="handleInputReset"
                                        @change="handleCustomInputChange"
										@blur="setInputValueByData(pickerDate)"
                                        @clear="clearValue"
                                        @key-enter="showPickerModal = false" @key-esc="showPickerModal = false"/>
                        </div>
                    </div>
                </card-text>
                <div class="flex w-full mt-16">
                    <btn v-if="showTimePicker" @click="showTimePicker = false" x-large class="flex-grow-1" tile>
                        <Icon :icon="icons.date"/>
                    </btn>
                    <btn v-else-if="props.time" @click="showTimePicker = true" x-large class="flex-grow-1" tile :disabled="!pickerDate.day">
                        <Icon :icon="icons.time"/>
                    </btn>
                    <btn @click="showPickerModal = false" x-large class="flex-grow-1" tile>
                        <Icon :icon="icons.check"/>
                    </btn>
                </div>
            </card>
        </popup>
    </div>
</template>

<script setup lang="ts">
    import {mdiCalendarRange, mdiClockOutline} from "@mdi/js";
    import {padNumber} from "@spoferan/spoferan-ts-core";
	import {checkIcon, helpIcon, resetIcon} from "../../../config";
	import {TextInputComponentEmits, type TextInputComponentProps, useTextInputComponent} from "../../../composables/useTextInputComponent";
	import {ref, watch, computed, nextTick} from "vue";
	import type {Ref} from "vue";
	import {useNuxtApp} from "#imports";

	const {$date} = useNuxtApp();

	const props = withDefaults(defineProps<{
		// Whether to run the picker in steps, starting with the year picker
		steps?: boolean,

		// Whether to also show a time picker
		time?: boolean,

		// Whether to also show a seconds picker
		seconds?: boolean,

		// Whether only minutes in 5 minutes interval can be chosen
		evenMinutes?: boolean,

		// A color to style the background of the picker
		color?: string,
	} & TextInputComponentProps>(), {
		type: 'date',
	});

	const emit = defineEmits(TextInputComponentEmits);

	const icons = {
		date: mdiCalendarRange,
		time: mdiClockOutline,
		reset: resetIcon,
		help: helpIcon,
		check: checkIcon
	};

	const inputElement: Ref<HTMLElement | null> = ref(null);

	const {
		submitValue,
		sizeableString,
		isActive,
		isRequired,
		isEmpty,
		hasError,
		hasPrefix,
		hasSuffix,
		errorMessage,
		handleKeydown,
		handleEnter,
		handleBlur,
		handleFocus,
		clearValue
	} = useTextInputComponent(inputElement, props, emit, {
		validationType: props.time ? 'datetime' : 'date'
	});
	watch(submitValue, (newValue) => {
		if (!isCustomInput.value) {
			inputValue.value = newValue ? $date(newValue).format(displayFormat.value) : null;

			// Reset the picker date when the value was reset to restart at year select
			if (!newValue) {
				pickerDate.value = {}
				pickerDateMode.value = props.steps ? 'year' : 'day'
			}
		} else if (!newValue) {
			showTimePicker.value = false
		}
	});
	watch(isActive, (newValue) => {
		if (newValue) {
			showPickerModal.value = true;
		}
	});

	const showPickerModal = ref(false);
	watch(showPickerModal, (newValue) => {
		if (!newValue) {
			// If the picker is closed but an invalid date was provided, we clear the value
			if (!pickerDate.value.year || !pickerDate.value.month || !pickerDate.value.day) {
				submitValue.value = null;
			}
			showTimePicker.value = false; // Reset the picker view back to the date picker
			handleEnter(); // Closing the picker modal shall be treated equally to hitting enter
			handleBlur();
		} else {
			inputValue.value = props.modelValue ? $date(props.modelValue).format(displayFormat.value) : null;
			handleCustomInputChange();
		}
	});

	const showTimePicker = ref(false);

	// Whether the user currently uses the custom input field to input a date
	const isCustomInput = ref(false);

	// The date and time selected on the picker
	type PickerDate = {
		year?: number,
		month?: number,
		day?: number,
		hour?: number,
		minute?: number,
		second?: number,
	}
	const pickerDate: Ref<PickerDate> = ref({});
	watch(pickerDate, (newValue: PickerDate) => {
		if (!newValue.year) {
			submitValue.value = null;
		} else {
			let formattedValue = `${padNumber(newValue.year ?? 0, 4)}-${padNumber(newValue.month ?? 0)}-${padNumber(newValue.day ?? 0)}`
			if (props.time) {
				formattedValue += ` ${padNumber(newValue.hour ?? 0)}:${padNumber(newValue.minute ?? 0)}:${padNumber(newValue.second ?? 0)}`
			}

			// We only want to set the submit value if the date value has changed but not the format
			if (!date.value || !date.value.isSame(formattedValue)) {
				submitValue.value = formattedValue;
			}
		}
	}, {deep: true});

	const pickerDateMode = ref(props.steps ? 'year' : 'day');

	const maskFormat = computed(() => {
		return props.time ? (props.seconds ? '##.##.#### ##:##:##' : '##.##.#### ##:##') : '##.##.####'
	});

	const displayFormat = computed(() => {
		return props.time ? (props.seconds ? 'DD.MM.YYYY HH:mm:ss' : 'DD.MM.YYYY HH:mm') : 'DD.MM.YYYY'
	});

	// The value the user inputs and sees on the form input
	const inputValue = ref(props.modelValue ? $date(props.modelValue).format(displayFormat.value) : null);

	const formatPlaceholder = computed(() => {
		return props.time ? (props.seconds ? 'dd.mm.yyyy hh:mm:ss' : 'dd.mm.yyyy hh:mm') : 'dd.mm.yyyy'
	});

	const date = computed(() => {
		return $date(submitValue.value ? submitValue.value : undefined);
	});

	function setInputValueByData(dateData: PickerDate) {
		if (!dateData.year) {
			return;
		}

		let date = padNumber(dateData.day ? dateData.day : 0) + '.' + padNumber(dateData.month ? dateData.month : 0) + '.' + padNumber(dateData.year ? dateData.year : 0, 4)
		if (props.time) {
			date += ' ' + padNumber(dateData.hour ? dateData.hour : 0) + ':' + padNumber(dateData.minute ? dateData.minute : 0)

			if (props.seconds) {
				date += ':' + padNumber(dateData.second ? dateData.second : 0)
			}
		}

		inputValue.value = date;
	}

	function handleSelectedPickerDate(dateData) {
		setInputValueByData(dateData)
		if (props.time) {
			showTimePicker.value = true;
		} else {
			showPickerModal.value = false;
		}
	}

	function handleSelectedPickerHour(dateData) {
		setInputValueByData(dateData)
	}

	function handleSelectedPickerMinute(dateData) {
		setInputValueByData(dateData)

		// We wait until the selected minute is propagated to the submit value before closing the picker as this will trigger 'key-enter' (submit) events
		nextTick(() => {
			showPickerModal.value = false;
		})
	}

	function handleCustomInputChange() {
		if (!inputValue.value) {
			return;
		}
		isCustomInput.value = true;

		const now = $date();

		let parts: PickerDate = {}
		const dimensionParts = inputValue.value.split(' ');
		const dateParts = dimensionParts[0].split('.');
		parts.day = parseInt(dateParts[0])
		parts.month = dateParts.length > 1 && dateParts[1] ? parseInt(dateParts[1]) : now.month()
		parts.year = dateParts.length > 2 && dateParts[2] ? parseInt(dateParts[2]) : now.year()

		if (props.time) {
			const timeParts = dimensionParts.length > 1 ? dimensionParts[1].split(':') : [0, 0, 0];
			parts.hour = timeParts.length > 0 && timeParts[0] ? parseInt(timeParts[0]) : 0
			parts.minute = timeParts.length > 1 && timeParts[1] ? parseInt(timeParts[1]) : 0
			parts.second = timeParts.length > 2 && timeParts[2] ? parseInt(timeParts[2]) : 0
		}

		const modifyingYear = (parts.year && parts.year !== pickerDate.value.year);
		const modifyingMonth = (parts.month && parts.month !== pickerDate.value.month);
		const modifyingDate = (parts.day && parts.day !== pickerDate.value.day) || modifyingMonth || modifyingYear
		const modifyingTime = (parts.hour && parts.hour !== pickerDate.value.hour) || (parts.minute && parts.minute !== pickerDate.value.minute)

		parts = validateDateParts(parts);
		Object.keys(parts).forEach(key => {
			pickerDate.value[key] = parts[key];
		})

		// There might be a case where nothing is modified, e.g., users inputs a separator ('.', ':'), on which we do not want to change the picker view
		if (modifyingTime) {
			showTimePicker.value = true;
		} else if (modifyingDate) {
			showTimePicker.value = false;
			pickerDateMode.value = 'day'
		}

		nextTick(function () {
			isCustomInput.value = false;
		});
	}

	function handleInputReset() {
		submitValue.value = null;
		showTimePicker.value = false;
	}

	function validateDateParts(parts: PickerDate): PickerDate {
		// Ensure that the year always has 4 digits
		if (parts.year !== undefined) {
			if (parts.year === 1) {
				parts.year = 1900
			} else if (parts.year < 1000) {
				parts.year = parseInt(padNumber(parts.year, 4, true))
			}
		}
		if (parts.day !== undefined) {
			if (parts.day > 31) {
				parts.day = 31
			} else if (parts.day < 1) {
				parts.day = 1
			}
		}

		if (parts.month !== undefined) {
			if (parts.month > 12) {
				parts.month = 12
			} else if (parts.month < 1) {
				parts.month = 1
			}
		}
		if (parts.hour !== undefined) {
			if (parts.hour > 23) {
				parts.hour = 23
			} else if (parts.hour < 0) {
				parts.hour = 0
			}
		}
		if (parts.minute !== undefined) {
			if (parts.minute > 59) {
				parts.minute = 59
			} else if (parts.minute < 0) {
				parts.minute = 0
			}
		}
		if (parts.second !== undefined) {
			if (parts.second > 59) {
				parts.second = 59
			} else if (parts.second < 0) {
				parts.second = 0
			}
		}

		if (props.evenMinutes) {
			parts.minute = Math.floor(parts.minute / 5) * 5; // Floor to prevent 60 minutes overflow complexity
			parts.second = 0;
		}

		return parts;
	}

	handleCustomInputChange();

</script>

<style>
/*********************
  Inputs
**********************/
/*********************
  Typography
**********************/
/*********************
  Inputs
**********************/
/*********************
  Shadows
**********************/
/*********************
  Layout
**********************/
/*********************
  Card
**********************/
/*********************
  Opacity
**********************/
/* ==========================================================================
   Mixins
   ========================================================================== */
.form-input {
  width: 100%;
  position: relative;
}
.form-input .form-input__content {
  width: 100%;
  min-height: 38px;
  box-sizing: border-box;
  border: 1px solid #d8d8d8;
  border-radius: 5px;
  background-color: #ffffff;
  display: flex;
  position: relative;
  align-items: center;
}
.form-input .form-input__content > label, .form-input .form-input__content > .form-input__field-wrapper, .form-input .form-input__content > .dropdown, .form-input .form-input__content > .dropdown > .dropdown__activator {
  height: 100%;
  width: 100%;
  min-height: inherit;
  display: flex;
  align-items: center;
  position: relative;
}
.form-input .form-input__content .form-input__label {
  position: absolute;
  font-size: 15px;
  top: 10px;
  line-height: 1.2;
  left: 4px;
  background: #ffffff;
  color: #5e6b6b;
  padding: 0 4px;
  transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  transform-origin: top left;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.form-input .form-input__content .form-input__field {
  width: 100%;
  font-family: nunito-sans, Arial, Helvetica, sans-serif;
  font-size: 15px;
  line-height: 1.2;
  min-height: 38px;
  padding: 4px 8px;
  border: none;
  outline: none;
  color: #525e5e;
  background-color: #ffffff;
  border-radius: 5px;
  display: flex;
  align-items: center;
}
.form-input .form-input__content .form-input__field .form-input__placeholder {
  color: #7D8484;
}
.form-input .form-input__content .form-input__field::-webkit-input-placeholder { /* Chrome/Opera/Safari */
  color: #7D8484;
  opacity: 1;
}
.form-input .form-input__content .form-input__field::-moz-placeholder { /* Firefox 19+ */
  color: #7D8484;
  opacity: 1;
}
.form-input .form-input__content .form-input__field:-ms-input-placeholder { /* IE 10+ */
  color: #7D8484;
  opacity: 1;
}
.form-input .form-input__content .form-input__field:-moz-placeholder { /* Firefox 18- */
  color: #7D8484;
  opacity: 1;
}
.form-input .form-input__content .form-input__field[type=search] {
  -webkit-appearance: none;
}
.form-input .form-input__content .form-input__field::-webkit-search-cancel-button {
  display: none;
}
.form-input .form-input__content .form-input__field-content {
  font-family: nunito-sans, Arial, Helvetica, sans-serif;
  font-size: 15px;
  line-height: 1.2;
  color: #525e5e;
}
.form-input .form-input__details {
  display: flex;
  width: 100%;
  height: 16px;
  margin-bottom: 8px;
  position: relative;
}
.form-input .form-input__details > .form-input__message {
  font-size: 11px;
  line-height: 1.1;
  text-align: left;
  font-weight: normal;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 1px 8px;
}
.form-input .form-input__details > .form-input__message:focus, .form-input .form-input__details > .form-input__message:hover {
  overflow: unset;
  text-overflow: unset;
  white-space: unset;
  position: absolute;
  z-index: 1;
  background: #ffffff;
}
.form-input.is-active .form-input__content {
  border-color: #008200;
}
.form-input.is-required .form-input__label:after {
  content: " *";
  color: #FA5555;
}
.form-input.is-disabled {
  opacity: 0.8;
}
.form-input.is-disabled .form-input__field {
  cursor: not-allowed;
}
.form-input:not(.is-empty) .form-input__content .form-input__label, .form-input.is-active .form-input__content .form-input__label {
  left: 4px !important;
  transform: translateY(-18px) scale(0.7);
  max-width: 120%;
}
.form-input:not(.is-empty).has-suffix .form-input__content .form-input__label, .form-input.is-active.has-suffix .form-input__content .form-input__label {
  padding-right: 0;
}
.form-input.has-success .form-input__content {
  border-color: #47a447 !important;
}
.form-input.has-success .form-input__message {
  color: #47a447;
}
.form-input.has-success .form-input__label {
  color: #47a447;
}
.form-input.has-error .form-input__content {
  border-color: #FA5555 !important;
}
.form-input.has-error .form-input__message {
  color: #FA5555;
}
.form-input.has-error .form-input__label {
  color: #FA5555;
}
.form-input.has-warning .form-input__content {
  border-color: #EB9E05 !important;
}
.form-input.has-warning .form-input__message {
  color: #EB9E05;
}
.form-input.has-warning .form-input__label {
  color: #EB9E05;
}
.form-input.has-prefix .form-input__content .form-input__field-wrapper {
  padding-left: 32px;
}
.form-input.has-prefix .form-input__content .form-input__label {
  left: 32px;
}
.form-input.has-prefix .form-input__prefix {
  position: absolute;
  top: 20px;
  transform: translateY(-50%);
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
}
.form-input.has-prefix .form-input__prefix .icon, .form-input.has-prefix .form-input__prefix .icon > svg {
  width: 18px;
  height: 18px;
}
.form-input.has-suffix .form-input__content .form-input__field-wrapper, .form-input.has-suffix .form-input__content .form-input__label {
  padding-right: 32px;
}
.form-input.has-suffix .form-input__suffix {
  position: absolute;
  top: 20px;
  transform: translateY(-50%);
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
}
.form-input.has-suffix .form-input__suffix .icon, .form-input.has-suffix .form-input__suffix .icon > svg {
  width: 18px;
  height: 18px;
  color: #7D8484;
}
.form-input.is-inverted .form-input__content {
  border-color: #f4f4f5;
  background-color: #f4f4f5;
}
.form-input.is-inverted .form-input__content .form-input__field {
  color: #525e5e;
  background-color: #f4f4f5;
}
.form-input.is-inverted .form-input__content .form-input__field .form-input__placeholder {
  color: #465050;
  opacity: 0.8;
}
.form-input.is-inverted .form-input__content .form-input__field::-webkit-input-placeholder { /* Chrome/Opera/Safari */
  color: #465050;
  opacity: 0.8;
}
.form-input.is-inverted .form-input__content .form-input__field::-moz-placeholder { /* Firefox 19+ */
  color: #465050;
  opacity: 0.8;
}
.form-input.is-inverted .form-input__content .form-input__field:-ms-input-placeholder { /* IE 10+ */
  color: #465050;
  opacity: 0.8;
}
.form-input.is-inverted .form-input__content .form-input__field:-moz-placeholder { /* Firefox 18- */
  color: #465050;
  opacity: 0.8;
}
.form-input.is-inverted .form-input__content .form-input__label {
  background: transparent;
}
.form-input.is-inverted:not(.is-empty) .form-input__content .form-input__label, .form-input.is-inverted.is-active .form-input__content .form-input__label {
  transform: translateY(-24px) scale(0.7);
}
.form-input.is-plain .form-input__content {
  border: none;
}
.form-input.is-plain .form-input__content .form-input__field {
  padding: 0 !important;
}
.form-input.is-plain .form-input__details .form-input__message {
  padding-left: 0;
  padding-right: 0;
}
.form-input.form-input-xs .form-input__content {
  min-height: 24px;
}
.form-input.form-input-xs .form-input__content .form-input__label {
  font-size: 11.5px;
  top: 5.1px;
}
.form-input.form-input-xs .form-input__content .form-input__field {
  min-height: 24px;
  font-size: 11.5px;
}
.form-input.form-input-xs .form-input__content textarea.form-input__field {
  min-height: auto;
}
.form-input.form-input-xs.has-prefix .form-input__prefix {
  top: 13px;
}
.form-input.form-input-xs.has-suffix .form-input__suffix {
  top: 13px;
}
.form-input.form-input-xs:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-xs.is-active .form-input__content .form-input__label {
  transform: translateY(-11px) scale(0.7);
}
.form-input.form-input-xs.is-inverted:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-xs.is-inverted.is-active .form-input__content .form-input__label {
  transform: translateY(-17px) scale(0.7);
}
.form-input.form-input-sm .form-input__content {
  min-height: 32px;
}
.form-input.form-input-sm .form-input__content .form-input__label {
  font-size: 12px;
  top: 8.8px;
}
.form-input.form-input-sm .form-input__content .form-input__field {
  min-height: 32px;
  font-size: 12px;
}
.form-input.form-input-sm .form-input__content textarea.form-input__field {
  min-height: auto;
}
.form-input.form-input-sm.has-prefix .form-input__prefix {
  top: 17px;
}
.form-input.form-input-sm.has-suffix .form-input__suffix {
  top: 17px;
}
.form-input.form-input-sm:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-sm.is-active .form-input__content .form-input__label {
  transform: translateY(-15px) scale(0.7);
}
.form-input.form-input-sm.is-inverted:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-sm.is-inverted.is-active .form-input__content .form-input__label {
  transform: translateY(-21px) scale(0.7);
}
.form-input.form-input-md .form-input__content {
  min-height: 38px;
}
.form-input.form-input-md .form-input__content .form-input__label {
  font-size: 15px;
  top: 10px;
}
.form-input.form-input-md .form-input__content .form-input__field {
  min-height: 38px;
  font-size: 15px;
}
.form-input.form-input-md .form-input__content textarea.form-input__field {
  min-height: auto;
}
.form-input.form-input-md.has-prefix .form-input__prefix {
  top: 20px;
}
.form-input.form-input-md.has-suffix .form-input__suffix {
  top: 20px;
}
.form-input.form-input-md:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-md.is-active .form-input__content .form-input__label {
  transform: translateY(-18px) scale(0.7);
}
.form-input.form-input-md.is-inverted:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-md.is-inverted.is-active .form-input__content .form-input__label {
  transform: translateY(-24px) scale(0.7);
}
.form-input.form-input-lg .form-input__content {
  min-height: 46px;
}
.form-input.form-input-lg .form-input__content .form-input__label {
  font-size: 15px;
  top: 14px;
}
.form-input.form-input-lg .form-input__content .form-input__field {
  min-height: 46px;
  font-size: 15px;
}
.form-input.form-input-lg .form-input__content textarea.form-input__field {
  min-height: auto;
}
.form-input.form-input-lg.has-prefix .form-input__prefix {
  top: 24px;
}
.form-input.form-input-lg.has-suffix .form-input__suffix {
  top: 24px;
}
.form-input.form-input-lg:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-lg.is-active .form-input__content .form-input__label {
  transform: translateY(-22px) scale(0.7);
}
.form-input.form-input-lg.is-inverted:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-lg.is-inverted.is-active .form-input__content .form-input__label {
  transform: translateY(-28px) scale(0.7);
}
.form-input.form-input-xl .form-input__content {
  min-height: 50px;
}
.form-input.form-input-xl .form-input__content .form-input__label {
  font-size: 16px;
  top: 15.4px;
}
.form-input.form-input-xl .form-input__content .form-input__field {
  min-height: 50px;
  font-size: 16px;
}
.form-input.form-input-xl .form-input__content textarea.form-input__field {
  min-height: auto;
}
.form-input.form-input-xl.has-prefix .form-input__prefix {
  top: 26px;
}
.form-input.form-input-xl.has-suffix .form-input__suffix {
  top: 26px;
}
.form-input.form-input-xl:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-xl.is-active .form-input__content .form-input__label {
  transform: translateY(-24px) scale(0.7);
}
.form-input.form-input-xl.is-inverted:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-xl.is-inverted.is-active .form-input__content .form-input__label {
  transform: translateY(-30px) scale(0.7);
}
</style>